import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { GradingScheme } from 'app/library/grading-scheme/models/grading-scheme.model';
import { GradingSchemeState } from 'app/library/grading-scheme/models/grading-scheme.state';
import { AddPartialGrades } from 'app/library/partial-grade/actions';
import { PartialGrade } from 'app/library/partial-grade/models/partial-grade.model';
import { PartialGradeState } from 'app/library/partial-grade/models/partial-grade.state';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import {
    DeselectSelectedModulePartialGrades,
    FetchModulePartialGrades,
    FetchModulePartialGradesFailure,
    FetchModulePartialGradesSuccess,
    SearchModulePartialGrades,
    SelectFilteredModulePartialGrades,
    SetModulePartialGrades,
    ToggleSelectedModulePartialGrade,
    UnsetModulePartialGrades,
} from '../actions';
import { ModulePartialGradesService } from '../services/module-partial-grades.service';
import { ModulePartialGradesStateModel } from './module-partial-grades.state-model';

@State<ModulePartialGradesStateModel>({
    name: 'modulePartialGrades',
    defaults: new ModulePartialGradesStateModel(),
})
@Injectable()
export class ModulePartialGradesState extends SubentityState<PartialGrade> {
    @Selector([PartialGradeState.getEntities, GradingSchemeState.getEntity])
    static getEntities(state: ModulePartialGradesStateModel, partialGrades: PartialGrade[], gradingScheme: GradingScheme): PartialGrade[] {
        if (!partialGrades) {
            return null;
        }

        const toReturn = partialGrades.filter((partialGrade) => state.list.indexOf(partialGrade.id) !== -1);

        if (gradingScheme) {
            const gradesContainer = {};

            gradingScheme.grades.forEach((grade) => {
                gradesContainer[grade.id] = grade;
            });

            return toReturn.map<PartialGrade>((partialGrade) =>
                Object.assign<PartialGrade, PartialGrade, Partial<PartialGrade>>(new PartialGrade(), partialGrade, { grade: gradesContainer[partialGrade.gradeId] || null })
            );
        }

        return toReturn;
    }

    @Selector([PartialGradeState.getEntities, GradingSchemeState.getEntity, UserState.getEntities])
    static getEntitiesWithUser(state: ModulePartialGradesStateModel, partialGrades: PartialGrade[], gradingScheme: GradingScheme, users: User[]): PartialGrade[] {
        const toReturn = ModulePartialGradesState.getEntities(state, partialGrades, gradingScheme);

        if (toReturn && users) {
            const usersContainer = {};

            users.forEach((user) => {
                usersContainer[user.id] = user;
            });

            return toReturn.map<PartialGrade>((partialGrade) =>
                Object.assign<PartialGrade, PartialGrade, Partial<PartialGrade>>(new PartialGrade(), partialGrade, { user: usersContainer[partialGrade.participantId] || null })
            );
        }

        return toReturn;
    }

    @Selector([PartialGradeState.getEntities, GradingSchemeState.getEntity])
    static getFilteredEntities(state: ModulePartialGradesStateModel, partialGrades: PartialGrade[], gradingScheme: GradingScheme): PartialGrade[] {
        if (!partialGrades) {
            return null;
        }

        const toReturn = partialGrades.filter((partialGrade) => state.filtered.indexOf(partialGrade.id) !== -1);

        if (gradingScheme) {
            const gradesContainer = {};

            gradingScheme.grades.forEach((grade) => {
                gradesContainer[grade.id] = grade;
            });

            return toReturn.map<PartialGrade>((partialGrade) =>
                Object.assign<PartialGrade, PartialGrade, Partial<PartialGrade>>(new PartialGrade(), partialGrade, { grade: gradesContainer[partialGrade.gradeId] || null })
            );
        }

        return toReturn;
    }

    @Selector([PartialGradeState.getEntities, GradingSchemeState.getEntity, UserState.getEntities])
    static getFilteredEntitiesWithUser(state: ModulePartialGradesStateModel, partialGrades: PartialGrade[], gradingScheme: GradingScheme, users: User[]): PartialGrade[] {
        const toReturn = ModulePartialGradesState.getFilteredEntities(state, partialGrades, gradingScheme);

        if (toReturn && users) {
            const usersContainer = {};

            users.forEach((user) => {
                usersContainer[user.id] = user;
            });

            return toReturn.map<PartialGrade>((partialGrade) =>
                Object.assign<PartialGrade, PartialGrade, Partial<PartialGrade>>(new PartialGrade(), partialGrade, { user: usersContainer[partialGrade.participantId] || null })
            );
        }

        return toReturn;
    }

    @Selector()
    static getSelectedEntityIds(state: ModulePartialGradesStateModel): string[] {
        return state.selected;
    }

    constructor(private _modulePartialGradesService: ModulePartialGradesService, private store: Store) {
        super(null, 30);

        this._propstoSearch = [...PartialGrade.props_to_search];
    }

    @Action(SetModulePartialGrades)
    setAll(ctx: StateContext<ModulePartialGradesStateModel>, action: SetModulePartialGrades): void {
        this._setSubentities(ctx, action, AddPartialGrades);
    }

    @Action(UnsetModulePartialGrades)
    unsetAll(ctx: StateContext<ModulePartialGradesStateModel>): void {
        this._unsetSubentities(ctx);
    }

    @Action(FetchModulePartialGrades)
    fetchAll(ctx: StateContext<ModulePartialGradesStateModel>, action: FetchModulePartialGrades): void {
        this._fetchSubentities(
            ctx,
            action,
            SetModulePartialGrades,
            FetchModulePartialGradesSuccess,
            FetchModulePartialGradesFailure,
            this._modulePartialGradesService.getAll(action.payload)
        );
    }

    @Action(FetchModulePartialGradesSuccess)
    fetchAllSuccess(ctx: StateContext<ModulePartialGradesStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchModulePartialGradesFailure)
    fetchAllFailure(ctx: StateContext<ModulePartialGradesStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(ToggleSelectedModulePartialGrade)
    toggleSelect(ctx: StateContext<ModulePartialGradesStateModel>, action: ToggleSelectedModulePartialGrade): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredModulePartialGrades)
    selectAll(ctx: StateContext<ModulePartialGradesStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedModulePartialGrades)
    deselectAll(ctx: StateContext<ModulePartialGradesStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchModulePartialGrades)
    search(ctx: StateContext<ModulePartialGradesStateModel>, action: SearchModulePartialGrades): void {
        const partialGrades = this.store.selectSnapshot(PartialGradeState.getEntities);

        this._searchSubentities(ctx, action, partialGrades);
    }
}
